import { printQuoteDocument } from "./quotePrinting.js";
import ThemedSelect from "../shared/ThemedSelect.jsx";
import { useEffect, useRef, useState } from "react";
import { Alert, Badge, Button, Card, Form, Modal, Table } from "react-bootstrap";
import {
  getClaim,
  getQuoteDocument,
  listVariations,
  listClaimLines,
  populateNotesFromRequest,
  createClaimLine,
  updateClaimLine,
  deleteClaimLine,
  listEstimates,
  createEstimate,
  updateEstimate,
  deleteEstimate,
  listPcSums,
  pullPcSumsFromQuote,
  listMergeablePcSumLines,
  cloneEstimateLinesToRoom,
  deleteRoomLines,
  mergePcSumsFromQuote,
  createPcSum,
  updatePcSum,
  deletePcSum,
  listFinalQuote,
  createFinalQuote,
  updateFinalQuote,
  deleteFinalQuote,
  reorderList,
  getQuoteFees,
  resetQuoteFee,
} from "../../services/apiClient.js";
import PricedLineGrid from "./PricedLineGrid.jsx";
import { statusName } from "../../utils/status.js";
import FeeProfileLine from "./FeeProfileLine.jsx";
import { getSessionUser } from "../../services/pageDefaults.js";
import ApplyTemplateButton from "./ApplyTemplateButton.jsx";
import { SortableRows, SortableTr } from "../SortableRows.jsx";
import VariationsSection from "./VariationsSection.jsx";
import GrandTotals from "./GrandTotals.jsx";
import QuoteSignoff from "./QuoteSignoff.jsx";
import QuoteNotesSection from "./QuoteNotesSection.jsx";
import QuoteView, { DEFAULT_PRINT_OPTIONS } from "./QuoteView.jsx";
import QuotePrintOptions from "./QuotePrintOptions.jsx";
import { applyMode, detectMode, modeByKey } from "./quotePrintModes.js";
import TabHeader from "./TabHeader.jsx";

// The copied header's text while "Rename header to the new room" is on: the
// target room's name, matching the original's case (an all-caps header stays
// all-caps). No target chosen yet = show the original.
function autoHeaderText(original, targetRoomId, rooms) {
  const name = (rooms || []).find((r) => String(r.id) === String(targetRoomId))?.name;
  if (!name) return original || "";
  return (original || "") === (original || "").toUpperCase() ? name.toUpperCase() : name;
}

const ESTIMATE_API = {
  list: listEstimates,
  create: createEstimate,
  update: updateEstimate,
  remove: deleteEstimate,
  reorder: (claimId, ids) => reorderList(claimId, "estimates", ids),
};
const PC_SUM_API = {
  list: listPcSums,
  create: createPcSum,
  update: updatePcSum,
  remove: deletePcSum,
  reorder: (claimId, ids) => reorderList(claimId, "pc-sums", ids),
};
const FINAL_QUOTE_API = {
  list: listFinalQuote,
  create: createFinalQuote,
  update: updateFinalQuote,
  remove: deleteFinalQuote,
  reorder: (claimId, ids) => reorderList(claimId, "final-quote", ids),
};

// The Quote / Scope tab — the financial-works spine: Scope of Works (each line
// optionally flagged needs-confirmation → confirmed), estimate grid, variations,
// PC sums, final quote, sign-off gate, quote notes. See docs/archive/financials-map.md §6.
// (Order of Works retired 2026-07-12 — unused in practice; quotes.md.)

// One ordered, inline-editable text list (rows expand in place to edit, like the
// Progress tab). `withConfirmation` adds the scope-only confirmation flags.
function LineList({ claimId, kind, title, withConfirmation, templateTarget = null }) {
  const [items, setItems] = useState([]);
  const [editingId, setEditingId] = useState(null); // null | "new" | id
  const [form, setForm] = useState({ description: "", needs_confirmation: false, confirmed: false });
  const [error, setError] = useState("");
  const [focusTick, setFocusTick] = useState(0);
  const firstRef = useRef(null);

  function load() {
    return listClaimLines(claimId, kind)
      .then(setItems)
      .catch(() => setError("Unable to load."));
  }
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimId, kind]);

  // Focus the description when the editor opens / a new line is saved.
  useEffect(() => {
    if (editingId) firstRef.current?.focus();
  }, [focusTick, editingId]);

  // 1.0's "Create Work / Quote Request Details From Template" (Ruby's go-live
  // item, 2026-08-24): the job's request text from the insurer into line 1.
  // Server-guarded to an EMPTY list, like 1.0 — the 409 explains why.
  async function importRequestDetails() {
    setError("");
    try {
      await populateNotesFromRequest(claimId);
      load();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to copy the request details.");
    }
  }

  const [insertAfterId, setInsertAfterId] = useState(null);
  function startAdd(afterId = null) {
    setError("");
    setForm({ description: "", needs_confirmation: false, confirmed: false });
    setInsertAfterId(afterId);
    setEditingId("new");
    setFocusTick((t) => t + 1);
  }
  function startEdit(it) {
    setError("");
    setForm({
      description: it.description || "",
      needs_confirmation: !!it.needs_confirmation,
      confirmed: !!it.confirmed,
    });
    setEditingId(it.id);
  }
  async function save() {
    setError("");
    try {
      if (editingId === "new") {
        const created = await createClaimLine(claimId, kind, insertAfterId ? { ...form, after_id: insertAfterId } : form);
        await load();
        // Reopen a blank entry row for the next line — and when inserting,
        // walk DOWNWARD: the next insert follows the line just typed, so a
        // run of inserts reads top-to-bottom like typing a paragraph.
        if (insertAfterId && created?.id) setInsertAfterId(created.id);
        setForm({ description: "", needs_confirmation: false, confirmed: false });
        setFocusTick((t) => t + 1);
      } else {
        await updateClaimLine(claimId, kind, editingId, form);
        setEditingId(null);
        load();
      }
    } catch (err) {
      setError(err.response?.data?.error || "Unable to save.");
    }
  }
  async function remove(it) {
    if (!window.confirm("Delete this line?")) return;
    setError("");
    try {
      await deleteClaimLine(claimId, kind, it.id);
      load();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to delete.");
    }
  }

  const colSpan = withConfirmation ? 6 : 4;
  const set = (patch) => setForm((c) => ({ ...c, ...patch }));

  // Drag-and-drop: apply optimistically, persist the full order, revert on failure.
  const dragDisabled = editingId !== null;
  async function handleReorder(reordered) {
    const prev = items;
    setItems(reordered);
    try {
      await reorderList(claimId, kind, reordered.map((l) => l.id));
    } catch (err) {
      setItems(prev);
      setError(err.response?.data?.error || "Unable to save the new order.");
    }
  }

  function editRow(key, label) {
    return (
      <tr key={key}>
        <td />
        <td className="text-muted">{label}</td>
        <td>
          <Form.Control
            as="textarea"
            rows={2}
            size="sm"
            ref={firstRef}
            value={form.description}
            onChange={(e) => set({ description: e.target.value })}
          />
        </td>
        {withConfirmation ? (
          <>
            <td className="text-center">
              <Form.Check
                type="checkbox"
                checked={form.needs_confirmation}
                onChange={(e) => set({ needs_confirmation: e.target.checked })}
              />
            </td>
            <td className="text-center">
              <Form.Check
                type="checkbox"
                checked={form.confirmed}
                onChange={(e) => set({ confirmed: e.target.checked })}
              />
            </td>
          </>
        ) : null}
        <td className="text-end text-nowrap">
          <Button size="sm" className="me-2" onClick={save}>
            {key === "new" ? "Add" : "Save"}
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setEditingId(null)}>
            {key === "new" ? "Done" : "Cancel"}
          </Button>
        </td>
      </tr>
    );
  }

  function viewRow(it, idx) {
    return (
      <SortableTr key={it.id} id={it.id} disabled={dragDisabled}>
        <td className="text-muted">{idx + 1}</td>
        <td style={{ whiteSpace: "pre-wrap" }}>
          {it.description || <span className="text-muted">—</span>}
        </td>
        {withConfirmation ? (
          <>
            <td className="text-center">
              {it.needs_confirmation ? (
                <Badge bg="warning" text="dark">Needs</Badge>
              ) : (
                <span className="text-muted">—</span>
              )}
            </td>
            <td className="text-center">
              {it.confirmed ? <Badge bg="success">Yes</Badge> : <span className="text-muted">—</span>}
            </td>
          </>
        ) : null}
        <td className="text-end text-nowrap">
          <Button variant="outline-secondary" size="sm" className="me-2" data-hint="Insert a line after this one"
            onClick={() => startAdd(it.id)}>
            Insert
          </Button>
          <Button variant="outline-primary" size="sm" className="me-2" onClick={() => startEdit(it)}>
            Edit
          </Button>
          <Button variant="outline-danger" size="sm" onClick={() => remove(it)}>
            Delete
          </Button>
        </td>
      </SortableTr>
    );
  }

  return (
    <Card className="mb-3">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="mb-0">{title}</h5>
          <div className="d-flex gap-2">
            {/* Only offered on an EMPTY list: applying refuses a populated one
                (1.0's rule), so showing it beside a full scope would be a trap. */}
            {templateTarget && items.length === 0 && editingId === null ? (
              <ApplyTemplateButton
                claimId={claimId}
                target={templateTarget}
                label="Create From Template"
                onDone={load}
                onError={setError}
              />
            ) : null}
            {kind === "internal-notes" && items.length === 0 && editingId === null ? (
              // 1.0's QuotePage link, same guard: only offered onto an EMPTY
              // list, because the insurer's request text belongs at the top.
              <Button size="sm" variant="outline-primary" className="me-2" onClick={importRequestDetails}>
                Copy Request Details
              </Button>
            ) : null}
            {editingId === null ? (
              <Button size="sm" variant="success" onClick={() => startAdd(null)}>
                + Add
              </Button>
            ) : null}
          </div>
        </div>

        {error ? <div className="alert alert-danger">{error}</div> : null}

        <SortableRows items={items} onReorder={handleReorder} disabled={dragDisabled}>
        <Table striped bordered hover responsive size="sm" className="mb-0 align-middle">
          <thead className="table-surface">
            <tr>
              <th style={{ width: 36 }} />
              <th style={{ width: 40 }}>#</th>
              <th>Description</th>
              {withConfirmation ? (
                <th style={{ width: 110 }} className="text-center">Needs Conf.</th>
              ) : null}
              {withConfirmation ? (
                <th style={{ width: 90 }} className="text-center">Confirmed</th>
              ) : null}
              <th style={{ width: 160 }} className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {/* The insert EDITOR opens where the line will land (Robert
                2026-08-25: typing at the bottom while inserting after row 1
                "was very confusing" even though the save went to the right
                place) — the row number it shows is the one it will take. */}
            {items.flatMap((it, idx) => {
              const rows = [editingId === it.id ? editRow(it.id, idx + 1) : viewRow(it, idx)];
              if (editingId === "new" && insertAfterId === it.id) rows.push(editRow("new", idx + 2));
              return rows;
            })}
            {editingId === "new" && !insertAfterId ? editRow("new", items.length + 1) : null}
            {items.length === 0 && editingId !== "new" ? (
              <tr>
                <td colSpan={colSpan} className="text-center text-muted">No lines.</td>
              </tr>
            ) : null}
          </tbody>
        </Table>
        </SortableRows>
      </Card.Body>
    </Card>
  );
}

// Two safeguards KS asked for on 2026-09-10 that Karen Renall then asked to hold
// ("let's wait and see how it goes" / "yes probably but not now"). Built, switched
// off until the 2026-09-11 meeting decides; flip to turn on.
const INSPECTOR_PRINT_GATE = false;   // refuse Print while the inspector is missing on Details or Inspection
const WARN_QUOTING_ACCEPTED = false;  // ask before opening Print Quote on an Accepted or Do & Charge job

function ClaimQuoteTab({ claimId, onChanged }) {
  const [printOpts, setPrintOpts] = useState(DEFAULT_PRINT_OPTIONS);
  const [printDoc, setPrintDoc] = useState(null);
  const [printOpenedAsSow, setPrintOpenedAsSow] = useState(false);
  const [printError, setPrintError] = useState("");

  // Auto P&G/JMF fee-line states (quotes.md §8). Refreshed whenever the grid
  // reloads its rows, because the server may move a fee between states on any
  // save (edit → frozen, billed → frozen). null = no badges, which is also the
  // safe face for a failed load — nothing here renders as an empty list.
  const [feeStatus, setFeeStatus] = useState(null);
  const [feeError, setFeeError] = useState("");
  const [pgBreakdownOpen, setPgBreakdownOpen] = useState(false);
  // The Merge picker (1.0 ManagePCSums): which unpulled PC-Sum quote lines
  // are ticked. mergeRows null = closed; mergeReload carries the grid's own
  // reload so the new pair appears the moment the modal closes.
  const [mergeRows, setMergeRows] = useState(null);
  // CLONE ROOM (KS 2026-09-02). Trialled by named users for a day, open to
  // everyone from 2026-09-03 (the clone_room_users gate was deleted).
  const [clone, setClone] = useState(null); // { items, rooms, load } | null
  const [cloneFrom, setCloneFrom] = useState("");
  const [cloneTo, setCloneTo] = useState("");
  const [cloneChecked, setCloneChecked] = useState(new Set());
  const [cloneBusy, setCloneBusy] = useState(false);
  const [cloneAfter, setCloneAfter] = useState("");
  // Header rows carry their text INTO the copy, editable here first (76448's
  // first clone put a bold DINING/LOUNGE over an Exterior 3 block). dirty =
  // the user typed, so the smart prefill must never stomp it.
  const [cloneHeaders, setCloneHeaders] = useState({});
  const [cloneHeadersDirty, setCloneHeadersDirty] = useState(new Set());
  const [cloneAutoHeader, setCloneAutoHeader] = useState(true);
  const [delRoom, setDelRoom] = useState(null); // { items, load } | null
  const [delRoomId, setDelRoomId] = useState("");
  const [delBusy, setDelBusy] = useState(false);
  const [mergeChecked, setMergeChecked] = useState(new Set());
  const [mergeReload, setMergeReload] = useState(null);
  const refreshFees = () => getQuoteFees(claimId).then(setFeeStatus).catch(() => {});
  // A failed Reset/Restore must SAY so (the 409 "already invoiced" and the
  // accept-lock cases are real answers, not noise) — swallowed, the button
  // just looked dead. Its own state: LineList's error lives in that
  // component, out of scope here. Callers still reload after, so the grid
  // shows whatever the server really did.
  const handleFeeReset = async (kind) => {
    try {
      setFeeError("");
      setFeeStatus(await resetQuoteFee(claimId, kind));
    } catch (err) {
      setFeeError(err.response?.data?.error || "Unable to reset the fee line.");
    }
  };

  // Variations / PC Sums / Final Quote only apply once the job is ACCEPTED
  // (Karen 2026-07-15) — hidden before then UNLESS they already carry data
  // (never hide existing rows behind a status). Fail open on any error.
  const [advanced, setAdvanced] = useState(true);
  const [preAccept, setPreAccept] = useState({ variations: true, pcSums: true, finalQuote: true });
  useEffect(() => {
    getClaim(claimId)
      .then(async ({ claim }) => {
        const isAdvanced = ["A", "C"].includes(claim.current_status_code);
        setAdvanced(isAdvanced);
        if (!isAdvanced) {
          const [v, p, f] = await Promise.all([
            listVariations(claimId).catch(() => [null]),
            listPcSums(claimId).catch(() => [null]),
            listFinalQuote(claimId).catch(() => [null]),
          ]);
          setPreAccept({ variations: v.length > 0, pcSums: p.length > 0, finalQuote: f.length > 0 });
        }
      })
      .catch(() => setAdvanced(true));
  }, [claimId]);
  const showVariations = advanced || preAccept.variations;
  const showPcSums = advanced || preAccept.pcSums;
  const showFinalQuote = advanced || preAccept.finalQuote;

  useEffect(() => () => document.body.classList.remove("print-modal"), []);

  // mode: a print MODE to open in (quotePrintModes.js) — "sow" from the Print
  // Scope of Works button; none = the quote, the way it last went out.
  async function openPrint(mode = null) {
    setPrintError("");
    try {
      // A quote going out on a job that is already Accepted, or that is a Do & Charge job,
      // is nearly always a job that should first be rolled back to Quoted and re-typed as
      // R&Q (KS 2026-09-10, 76583: a quote went out while the job sat Accepted as a D&C).
      // Warn, don't refuse: the office may be printing a copy on purpose. A Scope of Works
      // is not the quote going out.
      if (WARN_QUOTING_ACCEPTED && mode !== "sow") {
        const { claim: c } = await getClaim(claimId);
        const accepted = ["A", "C"].includes(c.current_status_code);
        const dc = /do\s*&\s*charge/i.test(c.job_type_name || "");
        if (accepted || dc) {
          const why = [accepted ? `is ${statusName(c.current_status_code)}` : null, dc ? `is a ${c.job_type_name} job` : null].filter(Boolean).join(" and ");
          const ok = window.confirm(`This job ${why}. A quote is not normally sent on such a job: it should first be rolled back to Quoted and its job type changed to R&Q. Open the print anyway?`);
          if (!ok) return;
        }
      }
      const doc = await getQuoteDocument(claimId);
      // The print opens the way this quote LAST WENT OUT (saved at Print),
      // else from the insurer's suggested defaults (Vero jobs open with the
      // Vero-shape options ticked). Everything stays untickable in the panel —
      // the print is a choice, these just pick the right starting point.
      let opts = {
        ...DEFAULT_PRINT_OPTIONS,
        ...(doc.saved_print_options || doc.suggested_print_options || {}),
        // 7a.7 outranks the memory: All details is the INTERNAL print and
        // exposing margins must be a deliberate act EVERY time — a saved blob
        // from an internal print must never pre-arm the next one. Saved for
        // the usage data, never restored.
        allDetails: false,
      };
      // A remembered Scope of Works must not become the quote's starting point:
      // Print Quote always opens as a quote, Print Scope of Works as the SOW.
      if (mode) opts = applyMode(opts, mode);
      else if (opts.sow) opts = applyMode(opts, "quote");
      setPrintOpts(opts);
      setPrintOpenedAsSow(mode === "sow");
      setPrintDoc(doc);
      document.body.classList.add("print-modal");
    } catch {
      setPrintError("Unable to build the quote document.");
    }
  }
  function closePrint() {
    document.body.classList.remove("print-modal");
    setPrintDoc(null);
  }

  // Template reminders nobody filled in — the server finds them across both the
  // quote lines and the quote notes. Non-empty = the Print button is refused.
  const unfilled = printDoc?.unfilled_markers || [];
  // The quote is signed by the inspector; with none entered it goes out over the coordinator's
  // name. KS (2026-09-10, after three days of reminding the office): refuse the print until the
  // inspector is entered on BOTH the Details tab and the Inspection tab. A Scope of Works prints
  // no sign-off, so it is not held.
  const missingInspector = INSPECTOR_PRINT_GATE && printDoc && !printOpts.sow
    ? [
        !printDoc.claim.inspector_name ? "No inspector on the Details tab: the quote would be signed by the coordinator" : null,
        !printDoc.claim.inspection_inspector_name ? "No inspector on the Inspection tab" : null,
      ].filter(Boolean)
    : [];
  const printBlocked = unfilled.length > 0 || missingInspector.length > 0;

  // Preserve the SOW entry point independently of the editable document options.
  // Opening Print Scope of Works must never promote a job, even if its preview
  // options are subsequently changed to another document mode.
  function printDocument() {
    return printQuoteDocument({
      claimId,
      options: printOpts,
      openedAsSow: printOpenedAsSow,
      onChanged,
    });
  }

  const totalsRef = useRef(null);
  const scopeRef = useRef(null);
  const quoteRef = useRef(null);
  const variationsRef = useRef(null);
  const pcSumRef = useRef(null);
  const finalRef = useRef(null);
  const signoffRef = useRef(null);
  const notesRef = useRef(null);
  const quoteNotesRef = useRef(null);
  const jump = (ref) => ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const NAV = [
    ["Grand Totals", totalsRef],
    ["Notes", notesRef],
    ["Scope of Works", scopeRef],
    ["Quote Lines", quoteRef],
    showVariations ? ["Variations", variationsRef] : null,
    showPcSums ? ["PC Sums", pcSumRef] : null,
    showFinalQuote ? ["Final Quote", finalRef] : null,
    ["Sign-off", signoffRef],
    ["Quote Notes", quoteNotesRef],
  ].filter(Boolean);

  return (
    <>
      <TabHeader
        title="Quotes"
        help="quotes"
        actions={
          <>
            <span className="small text-muted">Go to:</span>
            {NAV.map(([label, ref]) => (
              <Button key={label} size="sm" variant="outline-secondary" onClick={() => jump(ref)}>
                {label}
              </Button>
            ))}
            {/* "All details" used to sit HERE, beside the button. It moved into
                the print options panel on 2026-08-11 — it is a print choice, it
                only ever affected the preview, and having one option out here
                and eight in there meant looking in two places. */}
            <div className="vr mx-2 align-self-stretch" />
            <Button size="sm" variant="outline-primary" onClick={() => openPrint()}>
              Print Quote
            </Button>
            {/* The works without the quote around them (KS 2026-09-08) — the
                same preview in Scope of Works mode; the panel can still
                switch documents once open. */}
            <Button size="sm" variant="outline-primary" className="ms-1" onClick={() => openPrint("sow")}>
              Print Scope of Works
            </Button>
          </>
        }
      />

      {printError ? <div className="alert alert-danger">{printError}</div> : null}

      {/* The client-facing P&G breakdown (KR 2026-08-20): the client wording
          scaled to THIS job's percent, dollars against its cost base — the
          numbers come ready-made from the fee status endpoint. The modal body
          is what gets SCREENSHOTTED and sent to the client (Robert
          2026-08-20): no internal labels or explainer text inside it, the
          title lives IN the padded body so it lines up with the table in the
          snip, and the ✕ stays alone in the header strip well clear of the
          screenshot zone. */}
      <Modal show={pgBreakdownOpen} onHide={() => setPgBreakdownOpen(false)} size="lg" centered>
        <Modal.Header closeButton className="border-0 pb-0" />
        <Modal.Body className="px-5 pb-5 pt-4">
          <h4 className="mb-4">P&amp;G breakdown</h4>
          {feeStatus?.pg_breakdown ? (
            <Table size="sm" bordered className="mb-0 align-middle">
              <thead className="table-surface">
                <tr><th>Component</th><th className="text-end" style={{ width: 90 }}>%</th><th className="text-end" style={{ width: 120 }}>Amount</th></tr>
              </thead>
              <tbody>
                {feeStatus.pg_breakdown.rows.map((r) => (
                  <tr key={r.name}>
                    <td>{r.name}</td>
                    <td className="text-end">{r.percent}%</td>
                    <td className="text-end">{new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" }).format(r.amount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="table-surface fw-semibold">
                  <td>Total — P&amp;G {feeStatus.pg_breakdown.percent}% of {new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" }).format(feeStatus.pg_breakdown.base_cost)}</td>
                  <td className="text-end">{feeStatus.pg_breakdown.percent}%</td>
                  <td className="text-end">{new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" }).format(feeStatus.pg_breakdown.rows.reduce((s, r) => s + r.amount, 0))}</td>
                </tr>
              </tfoot>
            </Table>
          ) : (
            <p className="text-muted mb-0">No client breakdown is configured (Quote Templates → P&amp;G (Client View)).</p>
          )}
        </Modal.Body>
      </Modal>

      {/* CLONE ROOM (KS 2026-09-02, asked during the Loop meeting; Loop
          demonstrated the same thing at 27:29). Robert's flow exactly: pick the
          room, every line in it arrives ticked, untick what should not carry,
          choose the room to copy into. */}
      <Modal show={!!clone} onHide={() => setClone(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="h6">Clone room</Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-2">
          <Form.Group className="mb-3">
            <Form.Label className="small text-muted mb-1">Copy lines from</Form.Label>
            <ThemedSelect
              size="sm"
              value={cloneFrom}
              onChange={(e) => {
                const room = e.target.value;
                setCloneFrom(room);
                // Everything in that room, ticked. Unticking is the point.
                const roomLines = (clone?.items || []).filter((l) => String(l.room_id) === room);
                setCloneChecked(new Set(roomLines.map((l) => l.id)));
                setCloneHeaders(Object.fromEntries(
                  roomLines.filter((l) => l.is_header).map((l) => [l.id, l.description || ""])
                ));
                setCloneHeadersDirty(new Set());
              }}
            >
              <option value="">Choose a room…</option>
              {[...new Map((clone?.items || []).map((l) => [String(l.room_id), l.room_name])).entries()]
                .map(([id, name]) => (
                  <option key={id} value={id}>
                    {name} ({(() => { const n = (clone?.items || []).filter((l) => String(l.room_id) === id).length; return `${n} quote line${n === 1 ? "" : "s"}`; })()})
                  </option>
                ))}
            </ThemedSelect>
          </Form.Group>

          {cloneFrom ? (
            <>
              <div className="small text-muted mb-1">{cloneChecked.size} of{" "}
                {(clone?.items || []).filter((l) => String(l.room_id) === cloneFrom).length} lines
              </div>
              <div style={{ maxHeight: 260, overflowY: "auto" }} className="border rounded mb-3">
                <Table size="sm" hover className="align-middle mb-0">
                  <tbody>
                    {(clone?.items || [])
                      .filter((l) => String(l.room_id) === cloneFrom)
                      .map((l) => (
                        <tr
                          key={l.id}
                          style={{ cursor: "pointer" }}
                          onClick={() =>
                            setCloneChecked((c) => {
                              const next = new Set(c);
                              if (next.has(l.id)) next.delete(l.id);
                              else next.add(l.id);
                              return next;
                            })
                          }
                        >
                          <td style={{ width: 30 }}>
                            <Form.Check type="checkbox" readOnly checked={cloneChecked.has(l.id)} />
                          </td>
                          <td style={{ whiteSpace: "pre-wrap" }}>
                            {l.is_header ? (
                              /* The copy's header text. With "Rename header"
                                 ticked it tracks the target room, read-only;
                                 untick to type your own. Typing must not
                                 toggle the row's tick. */
                              <Form.Control
                                size="sm"
                                className="fw-bold"
                                readOnly={cloneAutoHeader}
                                value={cloneAutoHeader
                                  ? autoHeaderText(l.description, cloneTo, clone?.rooms)
                                  : cloneHeaders[l.id] ?? l.description ?? ""}
                                data-hint={cloneAutoHeader
                                  ? "Following the room it is copied into — untick 'Rename header' below to type your own"
                                  : "What the copied header will say"}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setCloneHeaders((cur) => ({ ...cur, [l.id]: v }));
                                  setCloneHeadersDirty((cur) => new Set(cur).add(String(l.id)));
                                }}
                              />
                            ) : (
                              l.description || "—"
                            )}
                          </td>
                          <td className="text-end text-nowrap">
                            {new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" }).format(l.net_value || 0)}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </Table>
              </div>

              <Form.Group className="mb-3">
                <Form.Label className="small text-muted mb-1">Copy into</Form.Label>
                <ThemedSelect
                  size="sm"
                  value={cloneTo}
                  onChange={(e) => setCloneTo(e.target.value)}
                >
                  <option value="">Choose a room…</option>
                  {(clone?.rooms || [])
                    .filter((r) => String(r.id) !== cloneFrom)
                    .map((r) => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                </ThemedSelect>
              </Form.Group>

              {(clone?.items || []).some((l) => String(l.room_id) === cloneFrom && l.is_header && cloneChecked.has(l.id)) ? (
                /* Explicit, because an editable-looking textbox was not
                   (Robert: "textbox is not obvious it can be changed"). ON =
                   the copied header takes the new room's name, live in the box
                   above; OFF = the box opens for typing. */
                <Form.Check
                  type="checkbox"
                  id="clone-auto-header"
                  className="small mb-3"
                  label="Rename header to the new room"
                  checked={cloneAutoHeader}
                  onChange={(e) => setCloneAutoHeader(e.target.checked)}
                />
              ) : null}

              {/* WHERE the copies land (Robert 2026-09-02, after testing:
                  "insert after room x"). Bottom of the quote by default. */}
              <Form.Group>
                <Form.Label className="small text-muted mb-1">Place the copies</Form.Label>
                <ThemedSelect size="sm" value={cloneAfter} onChange={(e) => setCloneAfter(e.target.value)}>
                  <option value="">At the bottom of the quote lines</option>
                  {[...new Map((clone?.items || []).map((l) => [String(l.room_id), l.room_name])).entries()]
                    .map(([id, name]) => (
                      <option key={id} value={id}>After {name}'s lines</option>
                    ))}
                </ThemedSelect>
              </Form.Group>
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer className="py-2">
          <Button size="sm" variant="outline-secondary" onClick={() => setClone(null)}>Cancel</Button>
          <Button
            size="sm"
            disabled={cloneBusy || cloneChecked.size === 0 || !cloneTo}
            onClick={async () => {
              setCloneBusy(true);
              try {
                const tickedHeaders = (clone?.items || []).filter((l) => l.is_header && cloneChecked.has(l.id));
                const headerTexts = Object.fromEntries(
                  tickedHeaders.map((l) => [l.id, cloneAutoHeader
                    ? autoHeaderText(l.description, cloneTo, clone?.rooms)
                    : cloneHeaders[l.id] ?? l.description ?? ""])
                );
                const r = await cloneEstimateLinesToRoom(claimId, [...cloneChecked], cloneTo, cloneAfter || null, headerTexts);
                const reload = clone?.load;
                setClone(null);
                window.alert(r.message);
                await reload?.();
              } catch (e) {
                window.alert(e.response?.data?.error || "Unable to clone the room.");
              } finally {
                setCloneBusy(false);
              }
            }}
          >
            {cloneBusy ? "Cloning…" : "Clone"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* DELETE ROOM (Robert 2026-09-02) — the clone's undo: every ordinary
          line in one room goes at once. The service refuses whole when any of
          the room's lines is on an invoice. */}
      <Modal show={!!delRoom} onHide={() => setDelRoom(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="h6">Delete room</Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-2">
          <Form.Group className="mb-2">
            <Form.Label className="small text-muted mb-1">Delete every quote line in</Form.Label>
            <ThemedSelect size="sm" value={delRoomId} onChange={(e) => setDelRoomId(e.target.value)}>
              <option value="">Choose a room…</option>
              {[...new Map((delRoom?.items || []).map((l) => [String(l.room_id), l.room_name])).entries()]
                .map(([id, name]) => (
                  <option key={id} value={id}>
                    {name} ({(() => { const n = (delRoom?.items || []).filter((l) => String(l.room_id) === id).length; return `${n} quote line${n === 1 ? "" : "s"}`; })()})
                  </option>
                ))}
            </ThemedSelect>
          </Form.Group>
          {delRoomId ? (
            <div className="alert alert-danger py-2 small mb-0">
              {(() => {
                const doomed = (delRoom?.items || []).filter((l) => String(l.room_id) === delRoomId);
                const total = doomed.reduce((t, l) => t + Number(l.net_value || 0), 0);
                return <>Deletes <strong>{doomed.length} line{doomed.length === 1 ? "" : "s"}</strong> worth{" "}
                  <strong>{new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" }).format(total)}</strong>.
                  {" "}This can't be undone.</>;
              })()}
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer className="py-2">
          <Button size="sm" variant="outline-secondary" onClick={() => setDelRoom(null)}>Cancel</Button>
          <Button
            size="sm"
            variant="danger"
            disabled={delBusy || !delRoomId}
            onClick={async () => {
              setDelBusy(true);
              try {
                const r = await deleteRoomLines(claimId, delRoomId);
                const reload = delRoom?.load;
                setDelRoom(null);
                window.alert(r.message);
                await reload?.();
              } catch (e) {
                window.alert(e.response?.data?.error || "Unable to delete the room.");
              } finally {
                setDelBusy(false);
              }
            }}
          >
            {delBusy ? "Deleting…" : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* THE MERGE PICKER (1.0 ManagePCSums btnMergeChecked, KS 2026-08-29):
          tick the PC-Sum quote lines, and they become ONE pair — description
          joined with " + ", blended margin, exactly 1.0's rules
          (services/pcSums.js mergePcSumsFromQuote). Lines ALREADY PULLED are
          offered too (Robert 2026-08-31, matching 1.0): their existing pair is
          deleted and the merged pair takes its place — allowed only while the
          pair is unclaimed, and flagged when its Actual carries entered money
          the replacement would discard. */}
      <Modal show={!!mergeRows} onHide={() => setMergeRows(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="h6">Merge PC Sums from quote</Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-2">
          <div className="small text-muted mb-2">
            Ticked lines become one PC Sum pair (one Less, one Actual) instead of a pair each.
            A line marked <span className="badge text-bg-secondary">pulled</span> already has its
            pair — merging replaces that pair with the merged one.
          </div>
          <Table size="sm" hover className="align-middle mb-0">
            <tbody>
              {(mergeRows || []).map((r) => (
                <tr key={r.id} style={{ cursor: "pointer" }}
                  onClick={() => setMergeChecked((c) => {
                    const next = new Set(c);
                    if (next.has(r.id)) next.delete(r.id); else next.add(r.id);
                    return next;
                  })}>
                  <td style={{ width: 30 }}>
                    <Form.Check type="checkbox" readOnly checked={mergeChecked.has(r.id)} />
                  </td>
                  <td style={{ whiteSpace: "pre-wrap" }}>
                    {r.description || "—"}
                    {r.pulled ? <span className="badge text-bg-secondary ms-2">pulled</span> : null}
                    {r.pulled && Number(r.actual_net) !== 0 ? (
                      <div className="small text-danger">
                        Its Actual has {new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" }).format(r.actual_net)}{" "}
                        entered — replacing the pair discards it.
                      </div>
                    ) : null}
                  </td>
                  <td className="text-end text-nowrap">
                    {new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" }).format(r.net_value)}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer className="py-2">
          <span className="me-auto small text-muted">
            {mergeChecked.size} of {(mergeRows || []).length} selected
          </span>
          <Button size="sm" variant="outline-secondary" onClick={() => setMergeRows(null)}>Cancel</Button>
          <Button size="sm" disabled={mergeChecked.size === 0}
            onClick={async () => {
              try {
                const r = await mergePcSumsFromQuote(claimId, [...mergeChecked]);
                setMergeRows(null);
                window.alert(r.message);
                await mergeReload?.();
              } catch (e) {
                window.alert(e.response?.data?.error || "Unable to merge the PC Sums.");
              }
            }}>
            Merge
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Fullscreen, options on the LEFT, document on the right (Robert
          2026-09-08, from medispace's Proposal tab): the panel used to sit
          above the preview and squeeze it. */}
      <Modal show={!!printDoc} onHide={closePrint} fullscreen>
        <Modal.Header closeButton className="no-print">
          <Modal.Title>
            {modeByKey(detectMode(printOpts))?.label || "Quote"} — GPS#{printDoc?.claim?.claim_number}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-body-tertiary">
          <div className="row g-3 quote-print-layout">
          <div className="col-lg-3 col-xl-2 no-print">
            <div className="sticky-top pt-1" style={{ top: 0 }}>
              {printDoc ? <QuotePrintOptions options={printOpts} onChange={setPrintOpts} doc={printDoc} /> : null}
            </div>
          </div>
          <div className="col-lg-9 col-xl-10 quote-print-preview">
          {/* A quote must not leave the building still carrying a template
              reminder (Karen Sweatman 2026-08-04 — 20 did, most recently
              2026-08-01). The letter is still shown, because you cannot fix
              what you cannot see; it is the PRINT that is refused, and each
              offending line is named so it can be found. */}
          {unfilled.length ? (
            <div className="alert alert-danger no-print">
              <div className="fw-semibold mb-1">
                This quote still has {unfilled.length === 1 ? "a reminder" : `${unfilled.length} reminders`} to
                fill in — it cannot be printed until {unfilled.length === 1 ? "it is" : "they are"} replaced.
              </div>
              <ul className="mb-0 small">
                {unfilled.map((u, i) => (
                  <li key={`${u.where}-${i}`}>
                    <span className="text-muted">{u.where}:</span>{" "}
                    <code>{u.marker}</code> in “{u.text}”
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {printDoc ? <QuoteView doc={printDoc} options={printOpts} /> : null}
          </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="no-print">
          {/* Printing the quote MARKS THE JOB QUOTED — 20 years of 1.0 muscle
              memory (Robert 2026-07-15: "some fights are not worth having").
              Only fires from Inspected; already-Quoted+ jobs just print. */}
          {missingInspector.length > 0 ? (
            <div className="me-auto small text-danger">
              {missingInspector.map((m) => <div key={m}>{m}</div>)}
              <div className="text-muted">Enter the inspector on both tabs, then print.</div>
            </div>
          ) : null}
          <Button
            variant="outline-secondary"
            onClick={printDocument}
            disabled={printBlocked}
            data-hint={unfilled.length ? "Fill in the reminders listed above first" : missingInspector.length ? "Enter the inspector on the Details and Inspection tabs first" : undefined}
          >
            Print
          </Button>
          <Button variant="secondary" onClick={closePrint}>Close</Button>
        </Modal.Footer>
      </Modal>

      <div ref={totalsRef}>
        <Card className="mb-3">
          <Card.Body>
            <GrandTotals claimId={claimId} />
          </Card.Body>
        </Card>
      </div>
      <div ref={notesRef}>
        <LineList claimId={claimId} kind="internal-notes" title="ClaimsDesk Notes" />
      </div>
      <div ref={scopeRef}>
        <LineList claimId={claimId} kind="scope-lines" title="Scope of Works" withConfirmation templateTarget="scope" />
      </div>
      {/* Order of Works RETIRED 2026-07-12 (Karen Sweatman + data: zero open
          jobs carry lines, 5 of 903 jobs logged in 2026 — quotes.md). */}
      <div ref={quoteRef}>
        <Card className="mb-3">
          <Card.Body>
            {/* The claim's pricing profile, said out loud (fee-practice-analysis.md):
                what the policy table resolved for this insurer group + job type.
                Disabled + fees allowed = say nothing; the Enable button on the
                toolbar IS the message (Robert 2026-08-19). Shared with the
                Variation Costings grid, which shows it without the states. */}
            <FeeProfileLine
              feeStatus={feeStatus}
              states={["pg", "jmf", "profit", "rubbish"].map((k) => feeStatus?.[k]?.state)}
            />
            {feeError ? (
              <Alert variant="danger" className="py-2" dismissible onClose={() => setFeeError("")}>
                {feeError}
              </Alert>
            ) : null}
            <PricedLineGrid
              claimId={claimId}
              api={ESTIMATE_API}
              title="Quote Lines"
              locked={advanced && !getSessionUser()?.is_admin}
              requireWorkCentreQty
              allowSetMarkup
              defaultMarkup={feeStatus?.default_markup ?? null}
              feeStatus={feeStatus}
              onFeeReset={handleFeeReset}
              onLinesChanged={refreshFees}
              /* Overheads / VC Callout — named templates, so this renders a
                 picker. Unlike Scope it stays available on a populated grid:
                 1.0 appended overheads to a finished quote, which is the whole
                 point of them. */
              headerExtra={({ load, items, lookups }) => (
                <>
                  {/* CLONE ROOM (KS 2026-09-02). Pick a room, its lines arrive
                      ticked, untick what you don't want, choose the room to
                      copy into. */}
                  <Button
                      size="sm"
                      variant="outline-secondary"
                      className="me-1"
                      onClick={() => {
                        const scoped = (items || []).filter((l) => !l.line_kind && l.room_id);
                        if (scoped.length === 0) {
                          window.alert("No quote lines have a room set, so there is nothing to clone.");
                          return;
                        }
                        setClone({ items: scoped, rooms: lookups?.rooms || [], load });
                        setCloneFrom("");
                        setCloneTo("");
                        setCloneAfter("");
                        setCloneChecked(new Set());
                        setCloneHeaders({});
                        setCloneHeadersDirty(new Set());
                        setCloneAutoHeader(true);
                      }}
                    >
                      Clone Room
                    </Button>
                  <Button
                      size="sm"
                      variant="outline-danger"
                      className="me-1"
                      onClick={() => {
                        const scoped = (items || []).filter((l) => !l.line_kind && l.room_id);
                        if (scoped.length === 0) {
                          window.alert("No quote lines have a room set, so there is nothing to delete.");
                          return;
                        }
                        setDelRoom({ items: scoped, load });
                        setDelRoomId("");
                      }}
                    >
                      Delete Room
                    </Button>
                  {/* One click turns the auto fees on for a pre-upgrade job
                      (both states -> auto via the reset endpoint). Deliberately
                      no tidy-up of hand-entered fee lines — the estimator
                      decides what the old lines become (Robert 2026-08-19). */}
                  {feeStatus && (["pg", "jmf", "profit", "rubbish"].some((k) => feeStatus[k].state === "disabled"))
                    && (["pg", "jmf", "profit", "rubbish"].some((k) => feeStatus[k].enabled)) ? (
                    <Button
                      size="sm"
                      variant="outline-success"
                      data-hint="Turn on the automatic P&G / JMF lines for this job — existing lines are left untouched"
                      onClick={async () => {
                        for (const kind of ["pg", "jmf", "profit", "rubbish"]) await handleFeeReset(kind);
                        await load();
                      }}
                    >
                      Enable automatic fees
                    </Button>
                  ) : null}
                  {feeStatus?.pg_breakdown && feeStatus.pg.enabled ? (
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      data-hint="The client-facing P&G breakdown for this job — client wording scaled to this job's percent, with dollars"
                      onClick={() => setPgBreakdownOpen(true)}
                    >
                      P&G breakdown
                    </Button>
                  ) : null}
                  {["pg", "jmf", "profit", "rubbish"].map((kind) =>
                    feeStatus?.[kind]?.state === "removed" ? (
                      <Button
                        key={kind}
                        size="sm"
                        variant="outline-secondary"
                        data-hint="This auto line was deleted — put it back and recalculate"
                        onClick={async () => { await handleFeeReset(kind); await load(); }}
                      >
                        Restore {{ pg: "P&G", jmf: "JMF", profit: "Profit", rubbish: "Rubbish" }[kind]}
                      </Button>
                    ) : null
                  )}
                  <ApplyTemplateButton claimId={claimId} target="estimates" onDone={load} />
                </>
              )}
            />
          </Card.Body>
        </Card>
      </div>
      {showVariations ? (
        <div ref={variationsRef}>
          <Card className="mb-3">
            <Card.Body>
              <VariationsSection claimId={claimId} />
            </Card.Body>
          </Card>
        </div>
      ) : null}
      {showPcSums ? (
        <div ref={pcSumRef}>
          <Card className="mb-3">
            <Card.Body>
              {/* pairField: a PC Sum and its Actual (legacy LinkedEstimateIDs) are
                  one unit — dragging either moves both, PC Sum staying first. */}
              <PricedLineGrid
                claimId={claimId}
                api={PC_SUM_API}
                title="PC Sums"
                costTypeField="kind"
                pairField="pair_group"
                confirmDelete={(it) => it.pair_group
                  ? "Delete this PC Sum pair? The PC Sum and its Actual are one unit — both lines will be deleted."
                  : null}
                headerExtra={({ load }) => (
                  /* MANAGE PC SUMS (KS's flow, 2026-08-25): pulls every
                     PC-Sum-cost-typed quote line into a linked Less/Actual
                     pair — the Less backs out the quoted sell, BOTH sides at
                     the line's own margin (KS 2026-08-26). The order
                     auto-create gate counts these, so these buttons are what
                     the red message sends people to. Merge (KS 2026-08-29,
                     1.0's btnMergeChecked): chosen lines become ONE pair —
                     76441's vinyl lines are the example. */
                  <>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={async () => {
                        const r = await pullPcSumsFromQuote(claimId).catch((e) => ({ message: e.response?.data?.error || "Unable to pull PC Sums." }));
                        window.alert(r.message);
                        await load();
                      }}
                    >
                      Pull PC Sums from quote
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      className="ms-1"
                      onClick={async () => {
                        try {
                          const r = await listMergeablePcSumLines(claimId);
                          if (r.blocked_message) { window.alert(r.blocked_message); return; }
                          if (!r.rows.length) { window.alert("No PC Sum quote lines can be merged — none exist, or their pairs are already claimed on a progress claim."); return; }
                          setMergeRows(r.rows);
                          setMergeChecked(new Set());
                          setMergeReload(() => load);
                        } catch (e) {
                          window.alert(e.response?.data?.error || "Unable to load the PC Sum lines.");
                        }
                      }}
                    >
                      Merge PC Sums…
                    </Button>
                  </>
                )}
              />
            </Card.Body>
          </Card>
        </div>
      ) : null}
      {showFinalQuote ? (
        <div ref={finalRef}>
          <Card className="mb-3">
            <Card.Body>
              <PricedLineGrid claimId={claimId} api={FINAL_QUOTE_API} title="Final Quote (override)" lean />
            </Card.Body>
          </Card>
        </div>
      ) : null}
      <div ref={signoffRef}>
        <Card className="mb-3">
          <Card.Body>
            <QuoteSignoff claimId={claimId} onChanged={onChanged} />
          </Card.Body>
        </Card>
      </div>
      {/* The page is LONG — a way back up without scrolling (the Go-to bar lives
          at the top, so once you're down here it can't help you). */}
      <div className="d-flex justify-content-end mb-3">
        <Button size="sm" variant="outline-secondary"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
          ↑ Top of page
        </Button>
      </div>
      {/* Bottom of the quote, like 1.0's expander — the notes print below the
          priced work when the quote report lands. */}
      <div ref={quoteNotesRef}>
        <Card className="mb-3">
          <Card.Body>
            <QuoteNotesSection claimId={claimId} />
          </Card.Body>
        </Card>
      </div>
    </>
  );
}

export default ClaimQuoteTab;
